/**
 * Phép tính bán token Fox (ADR-0100, ADR-0123), cùng cách làm tròn với `FoxPricing` của tenant-service:
 *
 * - token Fox của một lời gọi = token thật × hệ số của từng chiều (vào, đọc cache, ra), **làm tròn lên** (`tokenFoxVaoRa`);
 * - giá gói = giá ghi đè, không có thì token / 1.000.000 × đơn giá, **làm tròn nửa lên đến cent**;
 * - token mua được bằng một số tiền = số tiền / đơn giá × 1.000.000, **làm tròn xuống**;
 * - giá VND = giá USD × tỷ giá, **làm tròn nửa lên đến đồng**.
 *
 * Tính bằng `BigInt` trên số đã nhân lên số nguyên, không qua `number` thập phân: 0,1 + 0,2 không được lệch một
 * đồng so với số server ghi vào ảnh chụp. Hàm trả chuỗi thập phân như API, view tự định dạng.
 */
import type { AiTokenDurationUnit } from '../../tenant/types.ts';

/** Số tiền USD: tối đa 12 chữ số nguyên, 2 chữ số lẻ (`UsdAmount` của spec). */
export const MAU_USD = /^\d{1,12}(\.\d{1,2})?$/;
/** Đơn giá USD cho 1 triệu token Fox: tối đa 8 chữ số nguyên, 6 chữ số lẻ. */
export const MAU_DON_GIA = /^\d{1,8}(\.\d{1,6})?$/;
export const NAP_TOI_THIEU_USD = 1;
export const NAP_TOI_DA_USD = 1_000_000;
export const TOKEN_TOI_DA = 1_000_000_000_000;
export const MUC_NAP_TOI_DA = 10;


/** Chuỗi thập phân không âm → số nguyên đã nhân `10^le`; `null` khi sai dạng hoặc thừa chữ số lẻ. */
export function nhanLen(chuoi: string, le: number): bigint | null {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(chuoi.trim().replace(',', '.'));
  if (!m) return null;
  const phan = m[2] ?? '';
  if (phan.length > le) return null;
  return BigInt(m[1]!) * 10n ** BigInt(le) + BigInt(phan.padEnd(le, '0') || '0');
}

/** Số nguyên đã nhân `10^le` → chuỗi đủ `le` chữ số lẻ. */
function thapPhan(n: bigint, le: number): string {
  if (le === 0) return n.toString();
  const s = n.toString().padStart(le + 1, '0');
  return `${s.slice(0, -le)}.${s.slice(-le)}`;
}

/** Chia làm tròn nửa lên (số không âm). */
const chiaNuaLen = (a: bigint, b: bigint) => (a * 2n + b) / (b * 2n);

/**
 * Giá USD hiệu lực của một gói: `ghiDe` nếu có, không thì `foxTokens / 1e6 × donGia`, làm tròn nửa lên đến cent.
 * `null` khi thiếu cả hai (gói chưa gửi yêu cầu được).
 */
export function giaGoiUsd(foxTokens: number, ghiDe: string | null, donGia: string | null): string | null {
  if (ghiDe !== null && ghiDe.trim() !== '') {
    const c = nhanLen(ghiDe, 2);
    return c === null ? null : thapPhan(c, 2);
  }
  if (donGia === null) return null;
  const micro = nhanLen(donGia, 6);
  if (micro === null) return null;
  // foxTokens × (micro / 1e6) / 1e6 USD = foxTokens × micro / 1e12 USD = foxTokens × micro / 1e10 cent
  return thapPhan(chiaNuaLen(BigInt(foxTokens) * micro, 10_000_000_000n), 2);
}

/** Token Fox mua được bằng `soTienUsd` theo đơn giá, làm tròn xuống; `null` khi một trong hai sai dạng. */
export function tokenTuSoTien(soTienUsd: string, donGia: string): number | null {
  const cent = nhanLen(soTienUsd, 2);
  const micro = nhanLen(donGia, 6);
  if (cent === null || micro === null || micro === 0n) return null;
  // (cent / 100) / (micro / 1e6) × 1e6 = cent × 1e10 / micro
  return Number((cent * 10_000_000_000n) / micro);
}

/** Giá VND của `usd` theo tỷ giá, làm tròn nửa lên đến đồng; `null` khi thiếu một trong hai. */
export function giaVnd(usd: string | null, tyGia: string | null | undefined): string | null {
  if (usd === null || tyGia === null || tyGia === undefined) return null;
  const cent = nhanLen(usd, 2);
  const micro = nhanLen(tyGia, 6);
  if (cent === null || micro === null) return null;
  return chiaNuaLen(cent * micro, 100_000_000n).toString();
}

export type LoiSoTien = 'trong' | 'sai' | 'ngoai-khoang';

/** Kiểm số tiền nạp: số USD tối đa 2 chữ số lẻ, 1–1.000.000. */
export function kiemSoTien(chuoi: string): LoiSoTien | null {
  const s = chuoi.trim().replace(',', '.');
  if (s === '') return 'trong';
  if (!MAU_USD.test(s)) return 'sai';
  const v = Number(s);
  return v < NAP_TOI_THIEU_USD || v > NAP_TOI_DA_USD ? 'ngoai-khoang' : null;
}

/** Chuỗi số tiền đã chuẩn (`10,5` → `10.5`), dùng cho thân yêu cầu. */
export const chuanSoTien = (chuoi: string): string => chuoi.trim().replace(',', '.');

/** Thời hạn của gói nạp token và yêu cầu theo số tiền: 1, 3, 6, 12 tháng hoặc 1–4 tuần. */
export const THANG_HOP_LE = [1, 3, 6, 12] as const;
export const TUAN_HOP_LE = [1, 2, 3, 4] as const;

export function thoiHanHopLe(donVi: AiTokenDurationUnit, so: number): boolean {
  return donVi === 'MONTH' ? (THANG_HOP_LE as readonly number[]).includes(so) : (TUAN_HOP_LE as readonly number[]).includes(so);
}

/** Đơn giá: rỗng là bỏ đơn giá (`null`); sai dạng hoặc bằng 0 là lỗi. */
export function kiemDonGia(chuoi: string): { giaTri: string | null; loi: 'sai' | null } {
  const s = chuoi.trim().replace(',', '.');
  if (s === '') return { giaTri: null, loi: null };
  if (!MAU_DON_GIA.test(s) || Number(s) <= 0) return { giaTri: null, loi: 'sai' };
  return { giaTri: s, loi: null };
}

/** Mức nạp gợi ý gõ dạng `10, 50, 100`: số nguyên 1–1.000.000, không trùng, tối đa 10 mức. */
export function docMucNap(chuoi: string): { muc: number[]; loi: 'sai' | 'qua-nhieu' | null } {
  const phan = chuoi.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
  const muc: number[] = [];
  for (const p of phan) {
    if (!/^\d{1,7}$/.test(p)) return { muc: [], loi: 'sai' };
    const v = Number(p);
    if (v < 1 || v > NAP_TOI_DA_USD) return { muc: [], loi: 'sai' };
    if (!muc.includes(v)) muc.push(v);
  }
  return { muc, loi: muc.length > MUC_NAP_TOI_DA ? 'qua-nhieu' : null };
}

/** Form tạo, sửa gói nạp token. Token là chuỗi người gõ (cho phép dấu chấm phân nhóm). */
export interface FormGoiNap {
  name: string;
  tokens: string;
  donVi: AiTokenDurationUnit;
  soLuong: number;
  /** Giá ghi đè; rỗng là tính theo đơn giá. */
  gia: string;
}

export type LoiGoiNap = Partial<Record<'name' | 'tokens' | 'thoiHan' | 'gia', 'trong' | 'sai' | 'dai'>>;

/** Đọc số token người gõ (`20.000.000`, `20 000 000`); `null` khi không phải số nguyên. */
export function docToken(chuoi: string): number | null {
  const s = chuoi.trim().replace(/[.\s,_]/g, '');
  if (!/^\d+$/.test(s)) return null;
  const v = Number(s);
  return Number.isSafeInteger(v) ? v : null;
}

export function kiemGoiNap(f: FormGoiNap): LoiGoiNap {
  const loi: LoiGoiNap = {};
  const ten = f.name.trim();
  if (!ten) loi.name = 'trong';
  else if (ten.length > 120) loi.name = 'dai';
  const tok = docToken(f.tokens);
  if (f.tokens.trim() === '') loi.tokens = 'trong';
  else if (tok === null || tok < 1 || tok > TOKEN_TOI_DA) loi.tokens = 'sai';
  if (!thoiHanHopLe(f.donVi, f.soLuong)) loi.thoiHan = 'sai';
  const gia = f.gia.trim().replace(',', '.');
  if (gia !== '' && (!MAU_USD.test(gia) || Number(gia) <= 0)) loi.gia = 'sai';
  return loi;
}

/**
 * Hệ số hoà vốn của một chiều (ADR-0123 mục 7): giá vốn chiều đó (USD cho 1 triệu token thật) chia đơn giá USD cho 1
 * triệu token Fox, làm tròn nửa lên 3 chữ số. Không ghim vào khoảng 0,001–1000 của hệ số: số
 * thật mới cho biết biên. `null` khi thiếu giá vốn hoặc đơn giá. Dùng ở máy chủ giả; server tự tính.
 */
export function heSoHoaVon(giaVon: string | null, donGia: string | null): number | null {
  const d = donGia === null ? null : nhanLen(donGia, 6);
  const g = giaVon === null ? null : nhanLen(giaVon, 6);
  if (d === null || d === 0n || g === null) return null;
  return Number(chiaNuaLen(g * 1000n, d)) / 1000;
}

/**
 * Biên hiện tại của một chiều (ADR-0123 mục 7) = hệ số đang đặt / hệ số hoà vốn − 1, ví dụ 0,35 là "+35%". Âm là
 * đang trừ thấp hơn giá vốn. `null` khi chưa có hoà vốn (hoặc hoà vốn 0: model miễn phí, biên vô nghĩa).
 */
export function bienHienTai(heSo: number, hoaVon: number | null): number | null {
  if (hoaVon === null || hoaVon <= 0) return null;
  return heSo / hoaVon - 1;
}

/** Hệ số đang đặt thấp hơn hoà vốn: mỗi lượt gọi lỗ. */
export const duoiHoaVon = (heSo: number, hoaVon: number | null): boolean => hoaVon !== null && heSo < hoaVon;

/** Token thật của một lượt gọi, chia như cổng AI ghi (ADR-0123 mục 2). */
export interface TokenThat {
  /** Đầu vào thường và ghi cache. */
  vao: number;
  /** Đọc cache. */
  docCache: number;
  /** Đầu ra, gồm token suy nghĩ. */
  ra: number;
}

/** Ba hệ số của model; `cache` null là bằng đầu vào. */
export interface BaHeSo {
  vao: number;
  cache: number | null;
  ra: number;
}

/** Số đã nhân 1000 của hệ số (≤ 3 chữ số lẻ); hệ số lạ thì làm tròn lên cho không trừ thiếu. */
const phanNghin = (heSo: number): bigint => nhanLen(String(heSo), 3) ?? BigInt(Math.ceil(heSo * 1000));
const chiaLen = (a: bigint, b: bigint) => (a + b - 1n) / b;

/**
 * Token Fox của một lượt (ADR-0123 mục 2), cùng cách làm tròn với cổng AI:
 * đầu vào = ceil(vào × hệ số vào + đọc cache × hệ số cache), đầu ra = ceil(ra × hệ số ra), tổng = vào + ra.
 */
export function tokenFoxVaoRa(raw: TokenThat, heSo: BaHeSo): { vao: number; ra: number; tong: number } {
  const hv = phanNghin(heSo.vao);
  const hc = heSo.cache === null ? hv : phanNghin(heSo.cache);
  const tichVao = BigInt(Math.max(0, raw.vao)) * hv + BigInt(Math.max(0, raw.docCache)) * hc;
  const vao = Number(chiaLen(tichVao, 1000n));
  const ra = Number(chiaLen(BigInt(Math.max(0, raw.ra)) * phanNghin(heSo.ra), 1000n));
  return { vao, ra, tong: vao + ra };
}

/** Hiển thị một hệ số theo ngôn ngữ, tối đa 3 chữ số lẻ, không ký hiệu (`0.125` → `0,125` ở vi). */
export const dinhDangHeSo = (heSo: number, locale: string): string =>
  heSo.toLocaleString(locale, { maximumFractionDigits: 3 });

/**
 * Hiển thị giá vốn, đơn giá USD cho 1 triệu token theo ngôn ngữ: ít nhất 2, tối đa 6 chữ số lẻ, vì giá đọc cache hay
 * có 3 chữ số trở lên (`0.075` → `0,075` ở vi). Chuỗi sai dạng giữ nguyên.
 */
export function dinhDangGiaToken(chuoi: string, locale: string): string {
  if (nhanLen(chuoi, 6) === null) return chuoi;
  return Number(chuoi.trim().replace(',', '.')).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 6 });
}

/**
 * Hiển thị số tiền USD đủ hai chữ số lẻ theo ngôn ngữ (`50.00` → `50,00` ở vi, `50.00` ở en). Chuỗi sai dạng giữ
 * nguyên. Đơn vị do view ghép (`USD`).
 */
export function dinhDangUsd(chuoi: string, locale: string): string {
  const c = nhanLen(chuoi, 2);
  if (c === null) return chuoi;
  const nguyen = (c / 100n).toLocaleString(locale);
  const le = (c % 100n).toString().padStart(2, '0');
  const dau = (1.5).toLocaleString(locale).charAt(1);
  return `${nguyen}${dau}${le}`;
}
